import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { Analytics } from '@vercel/analytics/react';
import Navigation from './components/shared/Navigation';
import Dashboard from './components/dashboard/Dashboard';
import StatsPage from './components/stats/StatsPage';
import AddGameForm from './components/add-game/AddGameForm';
import ProfilePage from './components/profile/ProfilePage';
import SchedulePage from './components/schedule/SchedulePage';
import MilestonesPage from './components/milestones/MilestonesPage';
import { PlayerProvider } from './context/PlayerContext';
import { ThemeProvider } from './context/ThemeContext';
import { SeasonProvider } from './context/SeasonContext';
import MilestoneProvider from './context/MilestoneContext';
import { useAuth } from './context/AuthContext';
import AuthPage from './components/auth/AuthPage';
import PlayerSetup from './components/onboarding/PlayerSetup';
import ErrorBoundary from './components/shared/ErrorBoundary';
import { getPlayers } from './firebase/firestore';

// Safe area CSS setup
if (typeof document !== 'undefined') {
  // Fix mobile viewport height issues (iOS Safari, etc.)
  const setVh = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };
  
  // Set on load
  setVh();
  
  // Update on resize
  window.addEventListener('resize', setVh);
  
  // Clean up on module unmount
  if (typeof module !== 'undefined' && module.hot) {
    module.hot.dispose(() => {
      window.removeEventListener('resize', setVh);
    });
  }
}
function MainApp({ 
  activeTab, 
  setActiveTab, 
  isLoading, 
  setIsLoading, 
  isRefreshing, 
  fadeIn, 
  children 
}) {
  return (
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-dark-100 dark:text-gray-100" 
           style={{ minHeight: 'calc(var(--vh, 1vh) * 100)' }}>
        {/* Pull-to-refresh indicator */}
        {isRefreshing && (
          <div role="alert" aria-live="assertive" className="fixed top-0 left-0 right-0 z-50 flex justify-center p-2 bg-blue-500 text-white text-sm">
            <RefreshCw size={14} className="animate-spin mr-2" aria-hidden="true" /> Refreshing...
          </div>
        )}
        
        <main 
          className={`flex-grow overflow-y-auto pb-20 pt-safe-top px-safe-right px-safe-left 
                     ${fadeIn ? 'opacity-100' : 'opacity-0'} transition-opacity duration-150`}
          id="main-content"
        >
          {children}
        </main>
        
        <footer className="fixed bottom-0 left-0 right-0 z-10">
          <Navigation 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
          />
        </footer>
      </div>
  );
}

function App() {
  const [activeTab, setActiveTab] = useState('dashboard'); // Initial tab is dashboard
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fadeIn, setFadeIn] = useState(true);
  const [hasPlayers, setHasPlayers] = useState(null);
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  // Get current user from auth context
  const { currentUser, logout } = useAuth();

  // Pull-to-refresh functionality - use ref to avoid recreating callback
  const isRefreshingRef = useRef(false);

  const handlePullToRefresh = useCallback(() => {
    if (isRefreshingRef.current) return;

    isRefreshingRef.current = true;
    setIsRefreshing(true);
    // Simulate a refresh - in a real app, this would call API endpoints or refresh data
    setTimeout(() => {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }, 1200);
  }, []); // Empty dependencies - stable callback

  // Setup pull-to-refresh
  useEffect(() => {
    let touchStart = 0;
    let touchEnd = 0;
    const threshold = 150; // Minimum distance to trigger refresh
    const maxScroll = 5; // Maximum scroll position to allow pull

    const handleTouchStart = (e) => {
      touchStart = e.targetTouches[0].clientY;
    };

    const handleTouchMove = (e) => {
      touchEnd = e.targetTouches[0].clientY;
    };

    const handleTouchEnd = () => {
      if (window.scrollY <= maxScroll && touchStart && (touchEnd - touchStart > threshold)) {
        handlePullToRefresh();
      }
      touchStart = 0;
      touchEnd = 0;
    };

    document.addEventListener('touchstart', handleTouchStart, false);
    document.addEventListener('touchmove', handleTouchMove, false);
    document.addEventListener('touchend', handleTouchEnd, false);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handlePullToRefresh]); // Now handlePullToRefresh is stable
  
  // Effect to check if user has any player profiles
  useEffect(() => {
    const checkForPlayers = async () => {
      if (currentUser) {
        try {
          setIsLoading(true);
          
          // Check if we have a newly created player flag
          const newPlayerCreated = localStorage.getItem('newPlayerCreated');
          if (newPlayerCreated === 'true') {
            localStorage.removeItem('newPlayerCreated');
          }
          
          const players = await getPlayers(currentUser.uid);
          
          if (players.length > 0) {
            setHasPlayers(true);
          } else {
            setHasPlayers(false);
          }
        } catch (error) {
          console.error('Error checking for players:', error);
          setHasPlayers(false);
        } finally {
          setIsLoading(false);
          setInitialCheckDone(true);
        }
      } else {
        setInitialCheckDone(true);
      }
    };
    
    checkForPlayers();
    
    // Add location change listener to recheck for players when navigating
    const handleLocationChange = () => {
      checkForPlayers();
    };
    
    // Also check when window gains focus (user might have created a player in another tab)
    const handleFocus = () => {
      checkForPlayers();
    };
    
    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [currentUser]);
  
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    
    // Transition out
    setFadeIn(false);
    
    // Wait for animation to complete, then change tab
    setTimeout(() => {
      setActiveTab(tab);
      // Transition in
      setFadeIn(true);
    }, 150);
  };
  // If initial check is not done, show a loading spinner
  if (!initialCheckDone) {
    return (
      <ErrorBoundary>
        <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-100 dark:text-gray-100">
          <div className="text-center">
            <RefreshCw size={32} className="mx-auto animate-spin text-blue-600 mb-2" />
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <SeasonProvider> {/* Moved SeasonProvider outside of PlayerProvider */}
          <PlayerProvider>
            <MilestoneProvider>
              <Analytics debug={false} />
              <Router>
              <Routes>
                {/* Auth route - accessible only when not logged in */}
                <Route
                  path="/auth"
                  element={!currentUser ? <AuthPage /> : <Navigate to="/" replace />}
                />
            
                {/* Onboarding route - accessible only when logged in but no player profile */}
                <Route
                  path="/setup"
                  element={
                    !currentUser ? (
                      <Navigate to="/auth" replace />
                    ) : hasPlayers === false ? (
                      <PlayerSetup />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />

                {/* Protected routes that require authentication and player profile */}
                {/* Wrap each route in the MainApp layout when user is authenticated and has players */}
                <Route
                  path="/"
                  element={
                    !currentUser ? (
                      <Navigate to="/auth" replace />
                    ) : hasPlayers === false ? (
                      <Navigate to="/setup" replace />
                    ) : (
                      <MainApp
                        activeTab="dashboard"
                        setActiveTab={handleTabChange}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        isRefreshing={isRefreshing}
                        fadeIn={fadeIn}
                      >
                        <Dashboard isLoading={isLoading} setIsLoading={setIsLoading} isRefreshing={isRefreshing} />
                      </MainApp>
                    )
                  }
                />
            
                <Route
                  path="/stats"
                  element={
                    !currentUser ? (
                      <Navigate to="/auth" replace />
                    ) : hasPlayers === false ? (
                      <Navigate to="/setup" replace />
                    ) : (
                      <MainApp
                        activeTab="stats"
                        setActiveTab={handleTabChange}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        isRefreshing={isRefreshing}
                        fadeIn={fadeIn}
                      >
                        <StatsPage isLoading={isLoading} setIsLoading={setIsLoading} isRefreshing={isRefreshing} />
                      </MainApp>
                    )
                  }
                />
            
                {/* Schedule route */}
                <Route
                  path="/schedule"
                  element={
                    !currentUser ? (
                      <Navigate to="/auth" replace />
                    ) : hasPlayers === false ? (
                      <Navigate to="/setup" replace />
                    ) : (
                      <MainApp
                        activeTab="schedule"
                        setActiveTab={handleTabChange}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        isRefreshing={isRefreshing}
                        fadeIn={fadeIn}
                      >
                        <SchedulePage isLoading={isLoading} setIsLoading={setIsLoading} isRefreshing={isRefreshing} />
                      </MainApp>
                    )
                  }
                />

                {/* Add Game route */}
                <Route
                  path="/add-game"
                  element={
                    !currentUser ? (
                      <Navigate to="/auth" replace />
                    ) : hasPlayers === false ? (
                      <Navigate to="/setup" replace />
                    ) : (
                      <MainApp
                        activeTab="add-game"
                        setActiveTab={handleTabChange}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        isRefreshing={isRefreshing}
                        fadeIn={fadeIn}
                      >
                        <AddGameForm isLoading={isLoading} setIsLoading={setIsLoading} isRefreshing={isRefreshing} />
                      </MainApp>
                    )
                  }
                />

                {/* Milestones route */}
                <Route
                  path="/milestones"
                  element={
                    !currentUser ? (
                      <Navigate to="/auth" replace />
                    ) : hasPlayers === false ? (
                      <Navigate to="/setup" replace />
                    ) : (
                      <MainApp
                        activeTab="milestones"
                        setActiveTab={handleTabChange}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        isRefreshing={isRefreshing}
                        fadeIn={fadeIn}
                      >
                        <MilestonesPage isLoading={isLoading} setIsLoading={setIsLoading} isRefreshing={isRefreshing} />
                      </MainApp>
                    )
                  }
                />

                {/* Profile route */}
                <Route
                  path="/profile"
                  element={
                    !currentUser ? (
                      <Navigate to="/auth" replace />
                    ) : hasPlayers === false ? (
                      <Navigate to="/setup" replace />
                    ) : (
                      <MainApp
                        activeTab="profile"
                        setActiveTab={handleTabChange}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        isRefreshing={isRefreshing}
                        fadeIn={fadeIn}
                      >
                        <ProfilePage isLoading={isLoading} setIsLoading={setIsLoading} isRefreshing={isRefreshing} />
                      </MainApp>
                    )
                  }
                />

                {/* Catch-all route */}
                <Route
                  path="*"
                  element={
                    !currentUser ? (
                      <Navigate to="/auth" replace />
                    ) : hasPlayers === false ? (
                      <Navigate to="/setup" replace />
                    ) : (
                      <Navigate to="/" replace />
                    )
                  }
                />
              </Routes>
            </Router>
            </MilestoneProvider>
          </PlayerProvider>
        </SeasonProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
